import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Star,
  TrendingUp,
  BarChart3,
  PieChart,
  ArrowRightLeft,
  Clock,
  DollarSign,
  Users,
  FolderKanban,
  Loader2,
  Target,
  Layers,
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart as RePieChart, Pie, Cell, Legend,
  LineChart, Line, CartesianGrid,
  FunnelChart, Funnel, LabelList,
} from 'recharts';
import { toast } from 'sonner';
import { reportsApi } from '../lib/api';

const PERIOD_OPTIONS = [
  { value: 'this_week', label: 'This Week' },
  { value: 'this_month', label: 'This Month' },
  { value: 'this_quarter', label: 'This Quarter' },
  { value: 'ytd', label: 'Year to Date' },
  { value: 'last_year', label: 'Last Year' },
  { value: 'all_time', label: 'All Time' },
];

const COLORS = ['#f97316', '#2563eb', '#16a34a', '#eab308', '#8b5cf6', '#ef4444', '#06b6d4', '#ec4899'];
const FUNNEL_COLORS = ['#f97316', '#fb923c', '#fdba74', '#fde68a', '#bef264', '#22c55e', '#ef4444'];

const formatCurrency = (v) => {
  if (v >= 1000000) return `$${(v / 1000000).toFixed(1)}M`;
  if (v >= 1000) return `$${(v / 1000).toFixed(0)}K`;
  return `$${v.toLocaleString()}`;
};

const formatMonth = (m) => {
  const [y, mo] = m.split('-');
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return `${months[parseInt(mo) - 1]} ${y.slice(2)}`;
};

// Report card definitions
const REPORT_CARDS = [
  { id: 'pipeline_funnel', title: 'Pipeline Funnel', group: 'Sales Pipeline', icon: Target },
  { id: 'avg_time_stage', title: 'Avg Time in Stage', group: 'Sales Pipeline', icon: Clock },
  { id: 'won_vs_lost', title: 'Won vs Lost', group: 'Sales Pipeline', icon: ArrowRightLeft },
  { id: 'revenue_month', title: 'Revenue by Month', group: 'Revenue', icon: DollarSign },
  { id: 'pipeline_value_stage', title: 'Pipeline Value by Stage', group: 'Revenue', icon: BarChart3 },
  { id: 'avg_deal_size', title: 'Average Deal Size', group: 'Revenue', icon: TrendingUp },
  { id: 'leads_source', title: 'Leads by Source', group: 'Lead Performance', icon: PieChart },
  { id: 'lead_volume', title: 'Lead Volume Over Time', group: 'Lead Performance', icon: Users },
  { id: 'projects_phase', title: 'Projects by Phase', group: 'Project Health', icon: FolderKanban },
  { id: 'project_status', title: 'Active vs Completed', group: 'Project Health', icon: Layers },
];

// Individual chart components
function PipelineFunnelChart({ period }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    reportsApi.pipelineFunnel(period).then(r => setData(r.data)).catch(() => setData([]));
  }, [period]);
  if (!data) return <ChartLoader />;
  if (data.length === 0) return <EmptyState />;

  const funnelData = data.filter(d => d.stage !== 'Lost').map((d, i) => ({
    name: d.stage, value: d.count, fill: FUNNEL_COLORS[i] || '#94a3b8',
    conversion: d.conversion_rate ? `${d.conversion_rate}%` : '',
  }));

  return (
    <div className="space-y-3">
      <ResponsiveContainer width="100%" height={220}>
        <FunnelChart>
          <Tooltip formatter={(v) => [v, 'Leads']} />
          <Funnel dataKey="value" data={funnelData} isAnimationActive>
            <LabelList position="right" fill="#64748b" fontSize={12} formatter={(v) => v} dataKey="name" />
            <LabelList position="center" fill="#fff" fontSize={14} fontWeight={600} dataKey="value" />
          </Funnel>
        </FunnelChart>
      </ResponsiveContainer>
      <div className="flex flex-wrap gap-2 text-xs text-muted-foreground px-1">
        {data.filter(d => d.conversion_rate).map(d => (
          <span key={d.stage} className="bg-muted px-2 py-0.5 rounded">{d.stage}: {d.conversion_rate}% conv.</span>
        ))}
      </div>
    </div>
  );
}

function AvgTimeChart({ period }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    reportsApi.avgTimeInStage(period).then(r => setData(r.data)).catch(() => setData([]));
  }, [period]);
  if (!data) return <ChartLoader />;
  if (data.every(d => d.avg_hours === 0)) return <EmptyState msg="No stage transition data yet" />;

  return (
    <ResponsiveContainer width="100%" height={240}>
      <BarChart data={data} layout="vertical" margin={{ left: 20 }}>
        <CartesianGrid strokeDasharray="3 3" horizontal={false} />
        <XAxis type="number" tickFormatter={v => `${v}h`} />
        <YAxis type="category" dataKey="stage" width={90} tick={{ fontSize: 12 }} />
        <Tooltip formatter={(v) => [`${v} hours`, 'Avg Time']} />
        <Bar dataKey="avg_hours" fill="#f97316" radius={[0, 4, 4, 0]} />
      </BarChart>
    </ResponsiveContainer>
  );
}

function WonVsLostChart({ period }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    reportsApi.wonVsLost(period).then(r => setData(r.data)).catch(() => setData(null));
  }, [period]);
  if (!data) return <ChartLoader />;

  const pieData = [
    { name: 'Won', value: data.won.count, fill: '#22c55e' },
    { name: 'Lost', value: data.lost.count, fill: '#ef4444' },
  ];
  const hasData = data.won.count > 0 || data.lost.count > 0;

  return hasData ? (
    <div className="space-y-2">
      <div className="text-center">
        <span className="text-3xl font-bold text-foreground">{data.win_rate}%</span>
        <span className="text-sm text-muted-foreground ml-2">Win Rate</span>
      </div>
      <ResponsiveContainer width="100%" height={180}>
        <RePieChart>
          <Pie data={pieData} cx="50%" cy="50%" outerRadius={70} dataKey="value" label={({ name, value }) => `${name}: ${value}`}>
            {pieData.map((entry) => <Cell key={entry.name} fill={entry.fill} />)}
          </Pie>
          <Tooltip />
        </RePieChart>
      </ResponsiveContainer>
      <div className="flex justify-center gap-6 text-sm">
        <span className="text-green-600">Won: {formatCurrency(data.won.value)}</span>
        <span className="text-red-500">Lost: {formatCurrency(data.lost.value)}</span>
      </div>
    </div>
  ) : <EmptyState msg="No won or lost deals yet" />;
}

function RevenueByMonthChart({ period }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    reportsApi.revenueByMonth(period).then(r => setData(r.data)).catch(() => setData([]));
  }, [period]);
  if (!data) return <ChartLoader />;
  if (data.length === 0) return <EmptyState msg="No revenue data" />;

  const chartData = data.map(d => ({ ...d, label: formatMonth(d.month) }));
  return (
    <ResponsiveContainer width="100%" height={240}>
      <BarChart data={chartData}>
        <CartesianGrid strokeDasharray="3 3" vertical={false} />
        <XAxis dataKey="label" tick={{ fontSize: 11 }} />
        <YAxis tickFormatter={formatCurrency} tick={{ fontSize: 11 }} />
        <Tooltip formatter={(v) => [formatCurrency(v), 'Revenue']} />
        <Bar dataKey="revenue" fill="#22c55e" radius={[4, 4, 0, 0]} />
      </BarChart>
    </ResponsiveContainer>
  );
}

function PipelineValueChart({ period }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    reportsApi.pipelineValueByStage(period).then(r => setData(r.data)).catch(() => setData([]));
  }, [period]);
  if (!data) return <ChartLoader />;
  if (data.length === 0) return <EmptyState />;

  return (
    <ResponsiveContainer width="100%" height={240}>
      <BarChart data={data}>
        <CartesianGrid strokeDasharray="3 3" vertical={false} />
        <XAxis dataKey="stage" tick={{ fontSize: 11 }} />
        <YAxis tickFormatter={formatCurrency} tick={{ fontSize: 11 }} />
        <Tooltip formatter={(v, name) => [name === 'value' ? formatCurrency(v) : v, name === 'value' ? 'Value' : 'Count']} />
        <Bar dataKey="value" fill="#2563eb" radius={[4, 4, 0, 0]} />
      </BarChart>
    </ResponsiveContainer>
  );
}

function AvgDealSizeChart({ period }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    reportsApi.avgDealSize(period).then(r => setData(r.data)).catch(() => setData([]));
  }, [period]);
  if (!data) return <ChartLoader />;
  if (data.length === 0) return <EmptyState msg="No won deals" />;

  const chartData = data.map(d => ({ ...d, label: formatMonth(d.month) }));
  return (
    <ResponsiveContainer width="100%" height={240}>
      <LineChart data={chartData}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis dataKey="label" tick={{ fontSize: 11 }} />
        <YAxis tickFormatter={formatCurrency} tick={{ fontSize: 11 }} />
        <Tooltip formatter={(v) => [formatCurrency(v), 'Avg Deal Size']} />
        <Line type="monotone" dataKey="avg_deal_size" stroke="#8b5cf6" strokeWidth={2} dot={{ r: 4 }} />
      </LineChart>
    </ResponsiveContainer>
  );
}

function LeadsBySourceChart({ period }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    reportsApi.leadsBySource(period).then(r => setData(r.data)).catch(() => setData([]));
  }, [period]);
  if (!data) return <ChartLoader />;
  if (data.length === 0) return <EmptyState />;

  const pieData = data.map((d, i) => ({ ...d, fill: COLORS[i % COLORS.length] }));
  return (
    <ResponsiveContainer width="100%" height={260}>
      <RePieChart>
        <Pie data={pieData} cx="50%" cy="50%" outerRadius={80} dataKey="count" nameKey="source" label={({ source, count }) => `${source} (${count})`}>
          {pieData.map((d) => <Cell key={d.source} fill={d.fill} />)}
        </Pie>
        <Tooltip formatter={(v, name, props) => [v, props.payload.source]} />
        <Legend />
      </RePieChart>
    </ResponsiveContainer>
  );
}

function LeadVolumeChart({ period }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    reportsApi.leadVolume(period).then(r => setData(r.data)).catch(() => setData([]));
  }, [period]);
  if (!data) return <ChartLoader />;
  if (data.length === 0) return <EmptyState />;

  const chartData = data.map(d => ({ ...d, label: formatMonth(d.month) }));
  return (
    <ResponsiveContainer width="100%" height={240}>
      <LineChart data={chartData}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis dataKey="label" tick={{ fontSize: 11 }} />
        <YAxis tick={{ fontSize: 11 }} />
        <Tooltip formatter={(v) => [v, 'New Leads']} />
        <Line type="monotone" dataKey="count" stroke="#f97316" strokeWidth={2} dot={{ r: 4 }} />
      </LineChart>
    </ResponsiveContainer>
  );
}

function ProjectsByPhaseChart() {
  const [data, setData] = useState(null);
  useEffect(() => {
    reportsApi.projectsByPhase().then(r => setData(r.data)).catch(() => setData([]));
  }, []);
  if (!data) return <ChartLoader />;
  if (data.length === 0) return <EmptyState msg="No active projects" />;

  return (
    <ResponsiveContainer width="100%" height={240}>
      <BarChart data={data}>
        <CartesianGrid strokeDasharray="3 3" vertical={false} />
        <XAxis dataKey="phase" tick={{ fontSize: 10 }} angle={-20} textAnchor="end" height={50} />
        <YAxis tick={{ fontSize: 11 }} />
        <Tooltip formatter={(v, name) => [name === 'value' ? formatCurrency(v) : v, name === 'value' ? 'Value' : 'Projects']} />
        <Bar dataKey="count" fill="#06b6d4" radius={[4, 4, 0, 0]} />
      </BarChart>
    </ResponsiveContainer>
  );
}

function ProjectStatusChart() {
  const [data, setData] = useState(null);
  useEffect(() => {
    reportsApi.projectStatusSummary().then(r => setData(r.data)).catch(() => setData(null));
  }, []);
  if (!data) return <ChartLoader />;

  const pieData = [
    { name: 'Active', value: data.active.count, fill: '#2563eb' },
    { name: 'Completed', value: data.completed.count, fill: '#22c55e' },
  ];
  const hasData = pieData.some(d => d.value > 0);

  return hasData ? (
    <div className="space-y-3">
      <ResponsiveContainer width="100%" height={200}>
        <RePieChart>
          <Pie data={pieData} cx="50%" cy="50%" innerRadius={45} outerRadius={75} dataKey="value" label={({ name, value }) => `${name}: ${value}`}>
            {pieData.map((d) => <Cell key={d.name} fill={d.fill} />)}
          </Pie>
          <Tooltip />
        </RePieChart>
      </ResponsiveContainer>
      <div className="flex justify-center gap-6 text-sm">
        <span className="text-blue-600">Active: {formatCurrency(data.active.value)}</span>
        <span className="text-green-600">Completed: {formatCurrency(data.completed.value)}</span>
      </div>
    </div>
  ) : <EmptyState msg="No projects yet" />;
}

function ChartLoader() {
  return <div className="flex items-center justify-center h-48"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;
}

function EmptyState({ msg = 'No data for this period' }) {
  return <div className="flex items-center justify-center h-48 text-sm text-muted-foreground">{msg}</div>;
}

// Map card IDs to chart components
const CHART_COMPONENTS = {
  pipeline_funnel: PipelineFunnelChart,
  avg_time_stage: AvgTimeChart,
  won_vs_lost: WonVsLostChart,
  revenue_month: RevenueByMonthChart,
  pipeline_value_stage: PipelineValueChart,
  avg_deal_size: AvgDealSizeChart,
  leads_source: LeadsBySourceChart,
  lead_volume: LeadVolumeChart,
  projects_phase: ProjectsByPhaseChart,
  project_status: ProjectStatusChart,
};

export default function Reports() {
  const [period, setPeriod] = useState('all_time');
  const [favorites, setFavorites] = useState([]);
  const [loadingFavs, setLoadingFavs] = useState(true);

  useEffect(() => {
    reportsApi.getFavorites()
      .then(r => setFavorites(r.data || []))
      .catch(() => {})
      .finally(() => setLoadingFavs(false));
  }, []);

  const toggleFavorite = useCallback(async (cardId) => {
    const newFavs = favorites.includes(cardId)
      ? favorites.filter(f => f !== cardId)
      : [...favorites, cardId];
    setFavorites(newFavs);
    try {
      await reportsApi.updateFavorites(newFavs);
    } catch {
      toast.error('Failed to save favorite');
    }
  }, [favorites]);

  // Sort cards: favorites first, then original order
  const sortedCards = [...REPORT_CARDS].sort((a, b) => {
    const aFav = favorites.includes(a.id) ? 0 : 1;
    const bFav = favorites.includes(b.id) ? 0 : 1;
    return aFav - bFav;
  });

  // Group cards
  const groups = {};
  sortedCards.forEach(card => {
    groups[card.group] = groups[card.group] || [];
    groups[card.group].push(card);
  });

  // If favorites exist, show them as a separate section first
  const favCards = sortedCards.filter(c => favorites.includes(c.id));
  const nonFavGroups = {};
  sortedCards.filter(c => !favorites.includes(c.id)).forEach(card => {
    nonFavGroups[card.group] = nonFavGroups[card.group] || [];
    nonFavGroups[card.group].push(card);
  });

  if (loadingFavs) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="reports-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight" data-testid="reports-title">Reports</h1>
          <p className="text-sm text-muted-foreground mt-1">Analytics and insights for your business</p>
        </div>
        <Select value={period} onValueChange={setPeriod}>
          <SelectTrigger className="w-48 rounded-sm" data-testid="period-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {PERIOD_OPTIONS.map(o => (
              <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Favorite Cards */}
      {favCards.length > 0 && (
        <section>
          <div className="flex items-center gap-2 mb-3">
            <Star className="h-4 w-4 text-amber-500 fill-amber-500" />
            <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">Favorites</h2>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {favCards.map(card => (
              <ReportCard key={card.id} card={card} period={period} isFavorite onToggleFav={() => toggleFavorite(card.id)} />
            ))}
          </div>
        </section>
      )}

      {/* Grouped Cards */}
      {Object.entries(nonFavGroups).map(([group, cards]) => (
        <section key={group}>
          <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-3">{group}</h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {cards.map(card => (
              <ReportCard key={card.id} card={card} period={period} isFavorite={false} onToggleFav={() => toggleFavorite(card.id)} />
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}

function ReportCard({ card, period, isFavorite, onToggleFav }) {
  const ChartComponent = CHART_COMPONENTS[card.id];
  const Icon = card.icon;
  const needsPeriod = !['projects_phase', 'project_status'].includes(card.id);

  return (
    <Card className="rounded-sm overflow-hidden" data-testid={`report-card-${card.id}`}>
      <CardHeader className="pb-2 flex flex-row items-center justify-between space-y-0">
        <CardTitle className="text-sm font-medium flex items-center gap-2">
          <Icon className="h-4 w-4 text-muted-foreground" />
          {card.title}
        </CardTitle>
        <Button
          variant="ghost"
          size="sm"
          className="h-7 w-7 p-0"
          onClick={onToggleFav}
          data-testid={`fav-btn-${card.id}`}
        >
          <Star className={`h-4 w-4 ${isFavorite ? 'text-amber-500 fill-amber-500' : 'text-muted-foreground'}`} />
        </Button>
      </CardHeader>
      <CardContent className="pt-0">
        {ChartComponent && (needsPeriod ? <ChartComponent period={period} /> : <ChartComponent />)}
      </CardContent>
    </Card>
  );
}
